'use client';

import { c as _c } from "react/compiler-runtime";
import { trackEcommerce, trackEvent } from '@svs/nova-core/nextjs/analytics';
import type { Role } from '@svs/nova-core/nextjs/auth';
import { useDialog } from '@svs/nova-foundation/nextjs/dialog';
import { deleteCoupon, resetCoupon } from '@svs/nova-poolbetting/core/action/coupon';
import { getMarketplaceBalance, getRetailerBalance } from '@svs/nova-poolbetting/core/frameworks/nextjs/marketplace';
import { useCouponId, usePlaceCouponWager, usePlaceExternalCouponWager } from '@svs/nova-poolbetting/core/nextjs/coupon';
import { type Coupon, getCouponIndex } from '@svs/nova-poolbetting/schema/coupon';
import type { DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { Commission } from '@svs/nova-poolbetting/schema/input';
import type { ActivatedDrawId, MarketplaceBalance, RetailerBalanceResult } from '@svs/nova-poolbetting/schema/marketplace';
import type { ExternalWagerGuid } from '@svs/nova-poolbetting/schema/wager';
import { CouponForm } from '@svs/nova-poolbetting/ui/CouponForm';
import { usePathname } from 'next/navigation';
import { type PropsWithChildren, type ReactNode, type SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import { walletCachedContext } from '@/components/index.browser';
type CouponFormProps = PropsWithChildren & {
  commissionPercent?: Commission;
  activatedDrawId?: ActivatedDrawId;
  paymentUrls?: Record<string, string | undefined>;
  marketplaceWallets: MarketplaceBalance | RetailerBalanceResult | undefined;
  drawNumber: DrawNumber;
  product: Definition['drawProducts'][number];
  role?: Role;
  externalCoupon?: Coupon;
  externalWagerGuid?: ExternalWagerGuid;
};

/**
 * Coupon form manager that wraps component in a form to handle potential server actions.
 *
 * @param root0 props
 * @param root0.children jsx children
 * @param root0.commissionPercent optional commission percentage to be applied to the wager, used for retailer wallet case
 * @param root0.activatedDrawId optional activated draw id, used for retailer wallet case
 * @param root0.paymentUrls optional payment urls, used for redirecting to payment page in retailer wallet case
 * @param root0.marketplaceWallets optional wallet info, used for retailer wallet case
 * @param root0.drawNumber draw number, used for wallet case to get current balance
 * @param root0.product product module engine, used for wallet case to get current balance
 * @param root0.role user role, used to determine if the user is player or retailer, which affects how wallet balance is fetched and updated
 * @param root0.externalCoupon optional external coupon data, if provided external wager flow is used
 * @param root0.externalWagerGuid optional external wager guid, used for updating the external wager after successful placement
 * @returns a manager that handles form for coupon
 */
export default function CouponFormManager(t0) {
  const $ = _c(49);
  const {
    children,
    commissionPercent,
    activatedDrawId,
    paymentUrls,
    marketplaceWallets,
    drawNumber,
    product,
    role,
    externalCoupon,
    externalWagerGuid
  } = t0;
  const [couponId] = useCouponId();
  const [wallet] = useState(marketplaceWallets);
  const [shouldRefetchWallet, setShouldRefetchWallet] = useState(false);
  const hasPendingPayment = useRef(false);
  const hasInitialWalletSet = useRef(false);
  const pathname = usePathname();
  const {
    context: walletContext
  } = walletCachedContext;
  const context = useContext(walletContext);
  let t1;
  let t2;
  if ($[0] !== context || $[1] !== wallet) {
    t1 = () => {
      if (context?.setState && wallet && !hasInitialWalletSet.current) {
        context.setState(wallet);
        hasInitialWalletSet.current = true;
      }
    };
    t2 = [wallet, context];
    $[0] = context;
    $[1] = wallet;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let marketplacePaymentWallets;
  if ($[4] !== context) {
    marketplacePaymentWallets = {};
    if (context?.state && "activatedDraws" in context.state) {
      for (const draw of context.state.activatedDraws) {
        const {
          balance,
          activatedDrawId: walletActivatedDrawId
        } = draw;
        const sharePrice = Number.parseInt(draw.SharePrice, 10);
        const soldShares = balance / sharePrice;
        marketplacePaymentWallets[walletActivatedDrawId] = {
          balance,
          soldShares,
          sharePrice
        };
      }
    }
    if (context?.state && "activatedDraw" in context.state && context.state.activatedDraw) {
      const {
        currentPlayableAmount,
        sharePrice: sharePrice_0,
        soldShares: soldShares_0,
        activatedDrawId: retailerActivatedDrawId
      } = context.state.activatedDraw;
      let t3;
      if ($[6] !== currentPlayableAmount) {
        t3 = Number.parseInt(currentPlayableAmount, 10);
        $[6] = currentPlayableAmount;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      const balance_0 = t3;
      let t4;
      if ($[8] !== sharePrice_0) {
        t4 = Number.parseInt(sharePrice_0, 10);
        $[8] = sharePrice_0;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      const sharePriceNum = t4;
      let t5;
      if ($[10] !== balance_0 || $[11] !== sharePriceNum || $[12] !== soldShares_0) {
        t5 = {
          balance: balance_0,
          soldShares: soldShares_0,
          sharePrice: sharePriceNum
        };
        $[10] = balance_0;
        $[11] = sharePriceNum;
        $[12] = soldShares_0;
        $[13] = t5;
      } else {
        t5 = $[13];
      }
      marketplacePaymentWallets[retailerActivatedDrawId] = t5;
    }
    $[4] = context;
    $[5] = marketplacePaymentWallets;
  } else {
    marketplacePaymentWallets = $[5];
  }
  let t3;
  if ($[14] !== context?.state) {
    t3 = () => {
      if (context?.state !== undefined) {
        setShouldRefetchWallet(true);
      }
    };
    $[14] = context?.state;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const callbackOnPaymentMpShares = t3;
  let t4;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      hasPendingPayment.current = false;
    };
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const callbackOnPaymentDone = t4;
  let t5;
  let t6;
  if ($[17] !== activatedDrawId || $[18] !== context || $[19] !== drawNumber || $[20] !== product || $[21] !== role || $[22] !== shouldRefetchWallet) {
    t5 = () => {
      const refetchWalletData = async () => {
        setShouldRefetchWallet(false);
        if (role === "Player") {
          const result = await getMarketplaceBalance({
            product,
            drawNumber
          });
          if (result.status === "SUCCESS") {
            context?.setState(result.data);
          }
        }
        if (role === "Retailer" && activatedDrawId) {
          const result_0 = await getRetailerBalance({
            activatedDrawId
          });
          if (result_0.status === "SUCCESS" && result_0.data.activatedDraw) {
            context?.setState(result_0.data);
          }
        }
      };
      if (shouldRefetchWallet) {
        refetchWalletData();
      }
    };
    t6 = [shouldRefetchWallet, product, drawNumber, context, role, activatedDrawId];
    $[17] = activatedDrawId;
    $[18] = context;
    $[19] = drawNumber;
    $[20] = product;
    $[21] = role;
    $[22] = shouldRefetchWallet;
    $[23] = t5;
    $[24] = t6;
  } else {
    t5 = $[23];
    t6 = $[24];
  }
  useEffect(t5, t6);
  let t7;
  if ($[25] !== activatedDrawId || $[26] !== callbackOnPaymentMpShares || $[27] !== commissionPercent || $[28] !== marketplacePaymentWallets || $[29] !== paymentUrls || $[30] !== role) {
    t7 = {
      commissionPercent,
      activatedDrawId,
      paymentUrls,
      marketplaceWallets: marketplacePaymentWallets,
      callbackOnPaymentDone,
      callbackOnPaymentMpShares,
      role,
      trackTransaction: trackEcommerce,
      trackEventFunction: trackEvent
    };
    $[25] = activatedDrawId;
    $[26] = callbackOnPaymentMpShares;
    $[27] = commissionPercent;
    $[28] = marketplacePaymentWallets;
    $[29] = paymentUrls;
    $[30] = role;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  const {
    mutate: mutateCouponWager
  } = usePlaceCouponWager(t7);
  let t8;
  if ($[32] !== activatedDrawId || $[33] !== callbackOnPaymentMpShares || $[34] !== externalWagerGuid || $[35] !== marketplacePaymentWallets || $[36] !== paymentUrls || $[37] !== role) {
    t8 = {
      activatedDrawId,
      paymentUrls,
      marketplaceWallets: marketplacePaymentWallets,
      callbackOnPaymentDone,
      callbackOnPaymentMpShares,
      externalWagerGuid,
      role,
      trackTransaction: trackEcommerce,
      trackEventFunction: trackEvent
    };
    $[32] = activatedDrawId;
    $[33] = callbackOnPaymentMpShares;
    $[34] = externalWagerGuid;
    $[35] = marketplacePaymentWallets;
    $[36] = paymentUrls;
    $[37] = role;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  const {
    mutate: mutateExternalCouponWager
  } = usePlaceExternalCouponWager(t8);
  const {
    showDialog
  } = useDialog();
  let t9;
  if ($[39] !== couponId || $[40] !== externalCoupon || $[41] !== mutateCouponWager || $[42] !== mutateExternalCouponWager || $[43] !== pathname || $[44] !== showDialog) {
    t9 = event => {
      event.preventDefault();
      if (hasPendingPayment.current) {
        return;
      }
      if (!(event.nativeEvent.submitter instanceof HTMLButtonElement || event.nativeEvent.submitter instanceof HTMLInputElement)) {
        return;
      }
      const {
        submitter
      } = event.nativeEvent;
      if (submitter.name === "delete") {
        (async () => {
          const couponIndex = Number.parseInt(submitter.value, 10);
          if (!getCouponIndex(couponIndex)) {
            return;
          }
          trackEvent({
            category: "Nya tipsen",
            action: "Ta bort kupong",
            optLabel: pathname
          });
          await deleteCoupon({
            couponId,
            couponIndex
          });
        })();
        return;
      }
      if (submitter.name === "clear") {
        (async () => {
          const couponIndex_0 = Number.parseInt(submitter.value, 10);
          if (!getCouponIndex(couponIndex_0)) {
            return;
          }
          await resetCoupon({
            couponId,
            couponIndex: couponIndex_0
          });
        })();
        return;
      }
      hasPendingPayment.current = true;
      if (externalCoupon) {
        mutateExternalCouponWager({
          coupon: externalCoupon,
          showDialog
        });
        return;
      }
      mutateCouponWager({
        couponId,
        showDialog
      });
    };
    $[39] = couponId;
    $[40] = externalCoupon;
    $[41] = mutateCouponWager;
    $[42] = mutateExternalCouponWager;
    $[43] = pathname;
    $[44] = showDialog;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  const handleSubmit = t9;
  let t10;
  if ($[46] !== children || $[47] !== handleSubmit) {
    t10 = <CouponForm onSubmit={handleSubmit} id="coupon_form">{children}</CouponForm>;
    $[46] = children;
    $[47] = handleSubmit;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  return t10;
}